import { z } from 'zod';
import { AbandonedCheckoutDelaySchema } from './checkout.schema';
import type { ThemeDirection } from './theme.schema';

// Zod schema for merchant and tenant provisioning
export const provisionTenantSchema = z.object({
  name: z.string().min(2, 'Store name must be at least 2 characters'),
  slug: z.string().min(2, 'Slug must be at least 2 characters').regex(/^[a-z0-9-]+$/i, 'Slug must only contain alphanumeric characters and hyphens'),
  currency: z.string().length(3).default('MAD').optional(),
  ownerName: z.string().min(2, 'Owner name must be at least 2 characters'),
  ownerEmail: z.string().email('Invalid email address'),
  ownerPhone: z.string().optional(),
  webhookUrl: z.string().url('Invalid webhook URL').optional(),
  primaryLocale: z.string().default('fr-MA').optional()
});

export type ProvisionTenantDTO = z.infer<typeof provisionTenantSchema>;

// Store policy texts, deep-merged by the service so a PATCH may carry only the policies it changes.
const tenantPoliciesSchema = z
  .object({
    refundPolicy: z.string().max(50000).optional(),
    privacyPolicy: z.string().max(50000).optional(),
    termsOfService: z.string().max(50000).optional(),
    shippingPolicy: z.string().max(50000).optional()
  })
  .strict();

/**
 * The merchant's brand identity — the one thing every theme needs and the platform never stored.
 * It lives inside `settings` rather than in its own column because it is 1:1 with the tenant, is
 * never filtered or sorted on, and is read on the same first paint as the rest of settings, so a
 * column would buy a wider row on the storefront hot path and nothing else. Colours are absent on
 * purpose: `store_themes.config` already owns primaryColor/secondaryColor, and a second home for
 * the same pixel is a bug. The service merges settings shallowly, so a patch carrying `brand` must
 * carry the whole object — the same contract `policies` already lives under.
 */
/** The social networks a store can link to, in the order a footer draws their icons. */
export const SOCIAL_NETWORKS = ['instagram', 'facebook', 'tiktok', 'youtube', 'x', 'whatsapp'] as const;

/** One social network a store can link to. */
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

/** Where each of the store's social profiles lives: a full web address, a WhatsApp one a wa.me link. */
export const tenantSocialSchema = z
  .object(Object.fromEntries(SOCIAL_NETWORKS.map((network) => [network, z.string().url().max(500).optional()])) as Record<SocialNetwork, z.ZodOptional<z.ZodString>>)
  .strict();

export const tenantBrandSchema = z
  .object({
    name: z.string().max(255).optional(),
    logoUrl: z.string().url().max(1000).optional(),
    markUrl: z.string().url().max(1000).optional(),
    social: tenantSocialSchema.optional()
  })
  .strict();

// Shopify's Online Store preferences: what search engines and a shared link show for the store — its title, its
// description, and the picture a WhatsApp or Facebook preview carries.
export const tenantSeoSchema = z
  .object({
    title: z.string().trim().max(70).optional(),
    description: z.string().trim().max(320).optional(),
    imageUrl: z.string().url().max(1000).optional()
  })
  .strict();

export type TenantSeo = z.infer<typeof tenantSeoSchema>;

export type TenantBrand = z.infer<typeof tenantBrandSchema>;
export type TenantSocial = z.infer<typeof tenantSocialSchema>;

/**
 * The language a store is served in. The three the platform already serves everywhere else — the
 * admin's own language list, the tenant's shipped `supported_locales`, and the storefront's price
 * formatter all name Arabic, French and English and nothing besides — so this names them too, by
 * language rather than by locale: `ar` and `ar-MA` are one reading, and it is the reading a
 * storefront is laid out from.
 */
export const StoreLanguageEnum = z.enum(['ar', 'fr', 'en']);

export type StoreLanguage = z.infer<typeof StoreLanguageEnum>;

// Which way each served language is read, so no theme has to carry the answer for a language.
const LANGUAGE_DIRECTION: Readonly<Record<StoreLanguage, ThemeDirection>> = { ar: 'rtl', fr: 'ltr', en: 'ltr' };

// Reads the language a store chose out of its free-form settings bag, or null when it has chosen none.
export function storeLanguage(settings: unknown): StoreLanguage | null {
  const bag = settings as { language?: unknown } | null | undefined;
  const parsed = StoreLanguageEnum.safeParse(bag?.language);
  return parsed.success ? parsed.data : null;
}

// Reads the served language a locale names, "ar-MA" as Arabic, or null for one the storefront is not written in.
export function localeLanguage(locale: string | null | undefined): StoreLanguage | null {
  const parsed = StoreLanguageEnum.safeParse((locale ?? '').slice(0, 2).toLowerCase());
  return parsed.success ? parsed.data : null;
}

// Resolves the direction a storefront is served with: the store's own language, else the theme's declared one.
export function resolveStoreDirection(settings: unknown, themeDirection: ThemeDirection): ThemeDirection {
  const language = storeLanguage(settings);
  return language === null ? themeDirection : LANGUAGE_DIRECTION[language];
}

// Keys stored in the tenant settings JSON column; unknown keys are rejected. `codForm` is
// deliberately absent: only PUT /cod-form may write it, because keeping a field key's meaning
// stable needs the stored config to compare against, which a stateless patch schema cannot do.
// The service merges settings shallowly, so a patch that omits codForm leaves it untouched.
const tenantSettingsPatchSchema = z
  .object({
    codEnabled: z.boolean().optional(),
    // Once a theme is installed, this is what decides the storefront's reading direction — a preview's
    // own FR/AR toggle never writes here, so trying a reading on cannot change the shop that is live.
    language: StoreLanguageEnum.optional(),
    abandonedCheckoutDelayMinutes: AbandonedCheckoutDelaySchema.optional(),
    shippingFreeThreshold: z.number().min(0).optional(),
    contactEmail: z.string().email().max(255).optional(),
    contactPhone: z.string().max(50).optional(),
    address1: z.string().max(255).optional(),
    city: z.string().max(100).optional(),
    country: z.string().regex(/^[A-Z]{2}$/, 'Country must be a 2-letter uppercase ISO code').optional(),
    taxIncludedInPrices: z.boolean().optional(),
    // Shopify's Order ID format: what goes before and after the number; no spaces, so a number stays one word on a label.
    orderIdPrefix: z.string().max(10).regex(/^\S*$/, 'No spaces in an order ID prefix').optional(),
    orderIdSuffix: z.string().max(10).regex(/^\S*$/, 'No spaces in an order ID suffix').optional(),
    // Shopify's order processing: archive an order once it is fulfilled and paid, so the open list holds only what is left to do.
    autoArchiveOrders: z.boolean().optional(),
    defaultTaxRatePercent: z.number().min(0).max(100).optional(),
    brand: tenantBrandSchema.optional(),
    seo: tenantSeoSchema.optional(),
    policies: tenantPoliciesSchema.optional()
  })
  .strict();

// Partial tenant settings update; only fields that exist on the tenant row are accepted.
export const updateTenantSettingsSchema = z
  .object({
    name: z.string().min(2, 'Store name must be at least 2 characters').max(255).optional(),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter uppercase ISO code').optional(),
    primaryLocale: z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'Locale must look like fr or fr-MA').optional(),
    supportedLocales: z
      .array(z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'Locale must look like fr or fr-MA'))
      .min(1)
      .optional(),
    settings: tenantSettingsPatchSchema.optional()
  })
  .strict()
  .refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' });

export type UpdateTenantSettingsDto = z.infer<typeof updateTenantSettingsSchema>;

// Bare hostname: dot-separated lowercase labels and a 2+ letter TLD — no protocol, port, or path.
const HOSTNAME_RE = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

// Custom domain attach payload; input is trimmed and lowercased before the hostname check.
export const addDomainSchema = z
  .object({
    domain: z
      .string()
      .trim()
      .toLowerCase()
      .min(4)
      .max(255)
      // Shape only. Whether a hostname sits in the platform's own namespace is decided by the
      // server, which reads that namespace from its environment; this schema also runs in a browser.
      .regex(HOSTNAME_RE, 'Domain must be a bare hostname like shop.example.ma (no protocol or path)')
  })
  .strict();

// Primary-domain switch payload; the only accepted mutation is isPrimary: true.
export const setPrimaryDomainSchema = z
  .object({
    isPrimary: z.literal(true)
  })
  .strict();

export type AddDomainDto = z.infer<typeof addDomainSchema>;
export type SetPrimaryDomainDto = z.infer<typeof setPrimaryDomainSchema>;
